import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Card,
  CardBody,
  Flex,
  Icon,
  Image,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Button as SharedButton } from '@diamond/shared/ui';
import { usePaymentProofOcrForm } from '@diamond/sol-buyer/utils';
import EditOutlined from '@mui/icons-material/EditOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { DropzoneOcr } from './DropzoneOcr';
import { OcrPreviewCard } from './OcrPreviewCard';

interface PaymentProofUploaderOcrProps {
  accessToken: string;
  orderPaymentId: string;
  isAccordionOpen?: boolean;
  onAccordionChange?: (isOpen: boolean) => void;
  onSuccess?: () => void;
}

interface SubmittedProofsPreviewProps {
  proofs: {
    id: string;
    file_name: string;
    preview_url: string;
  }[];
  onEdit: () => void;
}

const SubmittedProofsPreview = ({
  proofs,
  onEdit,
}: SubmittedProofsPreviewProps) => (
  <Stack gap={4}>
    <Flex align="center" justify="space-between">
      <Text color="#999999" fontSize="16px" fontWeight={400} lineHeight="24px">
        Preview
      </Text>
      <Button
        variant="ghost"
        size="sm"
        color="blue"
        fontSize="12px"
        fontWeight={500}
        h="32px"
        minW="auto"
        px={2}
        iconSpacing={2}
        leftIcon={<Icon as={EditOutlined} boxSize="16px" />}
        onClick={onEdit}
      >
        Ubah
      </Button>
    </Flex>

    <Flex gap="24px" wrap="wrap">
      {proofs.map((proof) => (
        <Stack key={proof.id} gap={2} w="88px">
          <Image
            src={proof.preview_url}
            alt={proof.file_name}
            width="88px"
            height="88px"
            borderRadius="4px"
            borderWidth="1px"
            borderColor="#E5E5EA"
            objectFit="cover"
          />
          <Text
            color="#373f41"
            fontSize="12px"
            fontWeight={400}
            lineHeight="16px"
            noOfLines={1}
          >
            {proof.file_name}
          </Text>
        </Stack>
      ))}
    </Flex>
  </Stack>
);

export const PaymentProofUploaderOcr = ({
  accessToken,
  orderPaymentId,
  isAccordionOpen,
  onAccordionChange,
  onSuccess,
}: PaymentProofUploaderOcrProps) => {
  const {
    cards,
    submittedProofs,
    isSubmitting,
    isPollingTimedOut,
    canSubmit,
    form,
    uploadFiles,
    deleteFile,
    confirmAccountName,
    editSubmittedProofs,
    submitProof,
    resetPollingTimer,
  } = usePaymentProofOcrForm({
    accessToken,
    orderPaymentId,
    onSuccess,
  });

  const [internalAccordionOpen, setInternalAccordionOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const resolvedAccordionOpen =
    typeof isAccordionOpen === 'boolean'
      ? isAccordionOpen
      : internalAccordionOpen;

  const manualOverrides = form.watch('manual_overrides');
  const remainingFileSlots = Math.max(5 - cards.length, 0);

  const showInfoBanner = cards.length === 0 || isPollingTimedOut;
  const hasSubmittedProofs = submittedProofs.length > 0;

  const handleAccordionChange = (expandedIndex: number | number[]) => {
    const isOpen = Array.isArray(expandedIndex)
      ? expandedIndex.includes(0)
      : expandedIndex === 0;

    if (onAccordionChange) {
      onAccordionChange(isOpen);
    } else {
      setInternalAccordionOpen(isOpen);
    }
  };

  return (
    <FormProvider {...form}>
      <Card
        shadow="none"
        borderRadius="8px"
        borderWidth="1px"
        borderColor="gray.33"
      >
        <CardBody p={0}>
          <Accordion
            allowToggle
            index={resolvedAccordionOpen ? [0] : []}
            onChange={handleAccordionChange}
          >
            <AccordionItem border="none">
              <AccordionButton px={6} py={4}>
                <Flex align="center" gap={3} flex={1}>
                  <Icon as={ReceiptLongOutlined} boxSize="26px" color="blue" />
                  <Text
                    fontSize="20px"
                    fontWeight={600}
                    color="#373f41"
                    lineHeight="24px"
                  >
                    Bukti Pembayaran
                  </Text>
                </Flex>
                <AccordionIcon />
              </AccordionButton>

              <AccordionPanel px={{ base: 4, md: 6 }} pb={6} pt={0}>
                {hasSubmittedProofs ? (
                  <SubmittedProofsPreview
                    proofs={submittedProofs}
                    onEdit={editSubmittedProofs}
                  />
                ) : (
                  <>
                    {showInfoBanner && (
                      <Flex
                        bg="#FFF9E1"
                        borderRadius="4px"
                        px={4}
                        py={2}
                        gap={3}
                        align="center"
                      >
                        <Icon as={InfoOutlined} color="orange" boxSize="20px" />
                        <Text fontSize="12px" color="#131415" flex={1}>
                          {isPollingTimedOut
                            ? 'Bukti transfer masih dianalisa, coba lagi nanti.'
                            : 'Segera unggah bukti pembayaran agar PO diproses'}
                        </Text>
                        {isPollingTimedOut && (
                          <Button
                            size="sm"
                            variant="link"
                            color="blue"
                            onClick={resetPollingTimer}
                          >
                            Refresh
                          </Button>
                        )}
                      </Flex>
                    )}

                    {cards.length > 0 && (
                      <Stack gap={4} mt={showInfoBanner ? 4 : 0}>
                        {cards.map((card, index) => {
                          const fallbackFieldIndex = cards
                            .slice(0, index)
                            .filter(
                              (c) =>
                                c.state === 'manual_fallback' ||
                                c.state === 'needs_account_name'
                            ).length;
                          const manualOverrideIndex = manualOverrides.findIndex(
                            (override) => override.ocr_job_id === card.item.id
                          );
                          const fieldIndex =
                            manualOverrideIndex >= 0
                              ? manualOverrideIndex
                              : fallbackFieldIndex;
                          const manualOverride = manualOverrides[fieldIndex];

                          return (
                            <OcrPreviewCard
                              key={card.item.id}
                              item={card.item}
                              state={card.state}
                              fieldIndex={fieldIndex}
                              isDeleting={deletingId === card.item.id}
                              senderAccountNameOverride={
                                manualOverride?.sender_account_name
                              }
                              onConfirmAccountName={() =>
                                confirmAccountName(card.item.id)
                              }
                              onDelete={() => {
                                setDeletingId(card.item.id);
                                deleteFile(card.item.id).finally(() =>
                                  setDeletingId(null)
                                );
                              }}
                            />
                          );
                        })}
                      </Stack>
                    )}

                    <Stack gap={3} mt={6}>
                      <Text
                        fontSize={{ base: '18px', md: '20px' }}
                        fontWeight={500}
                        color="#373f41"
                        lineHeight={{ base: '26px', md: '28px' }}
                      >
                        Unggah Bukti Transfer
                      </Text>

                      {remainingFileSlots > 0 ? (
                        <DropzoneOcr
                          disabled={isSubmitting}
                          maxFiles={remainingFileSlots}
                          maxSizeMb={2}
                          onFilesSelected={uploadFiles}
                        />
                      ) : (
                        <Flex
                          align="center"
                          bg="#F1F8FF"
                          borderRadius="8px"
                          justify="center"
                          minH="96px"
                          px={4}
                        >
                          <Text color="blue" fontSize="16px" fontWeight={600}>
                            Maksimal 5 file telah terupload
                          </Text>
                        </Flex>
                      )}
                    </Stack>

                    <Flex justify="flex-end" mt={4}>
                      <SharedButton
                        variant="solid"
                        width={{ base: '100%', sm: '220px' }}
                        isDisabled={!canSubmit || isSubmitting}
                        isLoading={isSubmitting}
                        onClick={submitProof}
                      >
                        <span>Kirim</span>
                      </SharedButton>
                    </Flex>
                  </>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </CardBody>
      </Card>
    </FormProvider>
  );
};
